import React from "react";
import { User } from "firebase/auth";
import { motion, AnimatePresence } from "motion/react";
import {
  Shield,
  Building2,
  TrendingUp,
  CreditCard,
  Trophy,
  Lock,
  UserX,
  LogOut,
  LogIn,
  X,
  Settings,
  Sparkles,
  CloudLightning,
  QrCode,
  Users,
  ShoppingBag,
  History,
  ZoomIn,
  ZoomOut,
  RotateCcw,
  Upload,
  Sun,
  Clock,
  DollarSign,
  CalendarDays,
} from "lucide-react";
import { UserProfile } from "../../types/userProfile";
import { AppTheme } from "../../types";
import { ThemeSelector } from "./ThemeSelector";

interface AppSidebarProps {
  isSidebarOpen: boolean;
  setIsSidebarOpen: (open: boolean) => void;
  activeTab: string;
  setActiveTab: (tab: any) => void;
  adminSubTab: string;
  setAdminSubTab: (subTab: any) => void;
  isAdminMode: boolean;
  user: User | null;
  userProfile: UserProfile;
  handleGoogleLogin?: () => void;
  onOpenAuthModal?: () => void;
  handleGoogleLogout: () => void;
  zoomLevel: number;
  handleZoomIn: () => void;
  handleZoomOut: () => void;
  handleResetZoom: () => void;
  handleResetAllData: () => void;
  handleImportJSON: (e: React.ChangeEvent<HTMLInputElement>) => void;
  isFirebaseConfigured: () => boolean;
  theme: AppTheme;
  setTheme: React.Dispatch<React.SetStateAction<AppTheme>>;
}

export const AppSidebar: React.FC<AppSidebarProps> = ({
  isSidebarOpen,
  setIsSidebarOpen,
  activeTab,
  setActiveTab,
  adminSubTab,
  setAdminSubTab,
  isAdminMode,
  user,
  userProfile,
  handleGoogleLogin,
  onOpenAuthModal,
  handleGoogleLogout,
  zoomLevel,
  handleZoomIn,
  handleZoomOut,
  handleResetZoom,
  handleResetAllData,
  handleImportJSON,
  isFirebaseConfigured,
  theme,
  setTheme,
}) => {
  return (
    <AnimatePresence>
      {isSidebarOpen && (
        <>
          {/* Backdrop Overlay */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 0.5 }}
            exit={{ opacity: 0 }}
            onClick={() => setIsSidebarOpen(false)}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-40"
          />

          {/* Sliding Drawer */}
          <motion.aside
            initial={{ x: "-100%" }}
            animate={{ x: 0 }}
            exit={{ x: "-100%" }}
            transition={{ type: "spring", bounce: 0, duration: 0.3 }}
            className="fixed inset-y-0 left-0 w-72 bg-white dark:bg-[#041208] text-slate-900 dark:text-white border-r border-slate-200 dark:border-emerald-900/60 z-50 p-5 flex flex-col justify-between overflow-y-auto max-h-screen custom-scrollbar shadow-2xl space-y-6 transition-colors"
          >
            <div>
              {activeTab === "admin" ? (
                /* NAVEGAÇÃO ISOLADA DO SUPER ADMIN */
                <div>
                  <div className="flex items-center justify-between pb-4 border-b border-slate-200 dark:border-amber-500/30">
                    <div className="flex items-center gap-2.5">
                      <Shield size={24} className="text-amber-500 dark:text-amber-400" />
                      <div>
                        <h2 className="text-sm font-display font-black text-amber-600 dark:text-amber-300 uppercase tracking-tight">
                          SUPER ADMIN MASTER
                        </h2>
                        <p className="text-[9px] text-slate-500 dark:text-zinc-400 font-bold">Central de Gestão do Site</p>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => setIsSidebarOpen(false)}
                      className="p-1.5 rounded-lg hover:bg-slate-100 dark:hover:bg-zinc-800 text-slate-500 dark:text-zinc-400 hover:text-slate-900 dark:hover:text-white transition cursor-pointer"
                    >
                      <X size={18} />
                    </button>
                  </div>

                  <nav className="mt-5 space-y-2">
                    <p className="text-[10px] font-extrabold uppercase tracking-wider text-amber-600 dark:text-amber-400/80 px-2 mb-1">
                      Gerenciamento do Sistema
                    </p>

                    <button
                      type="button"
                      onClick={() => { setAdminSubTab("arenas_qualificacao"); setIsSidebarOpen(false); }}
                      className={`w-full px-3.5 py-2.5 rounded-xl font-display font-bold text-xs transition flex items-center gap-3 cursor-pointer ${
                        adminSubTab === "arenas_qualificacao" || adminSubTab === "quadras" || adminSubTab === "aprovacao_arenas"
                          ? "bg-amber-400 text-emerald-950 shadow-md font-extrabold"
                          : "text-slate-700 dark:text-zinc-300 hover:bg-slate-100 dark:hover:bg-zinc-800/80 hover:text-slate-900 dark:hover:text-white"
                      }`}
                    >
                      <Building2 size={16} className={adminSubTab === "arenas_qualificacao" || adminSubTab === "quadras" || adminSubTab === "aprovacao_arenas" ? "text-emerald-950" : "text-amber-500 dark:text-amber-400"} />
                      <span>1. Arenas & Qualificação</span>
                    </button>

                    <button
                      type="button"
                      onClick={() => { setAdminSubTab("financeiro_dre"); setIsSidebarOpen(false); }}
                      className={`w-full px-3.5 py-2.5 rounded-xl font-display font-bold text-xs transition flex items-center gap-3 cursor-pointer ${
                        adminSubTab === "financeiro_dre" || adminSubTab === "fluxo_financeiro"
                          ? "bg-amber-400 text-emerald-950 shadow-md font-extrabold"
                          : "text-slate-700 dark:text-zinc-300 hover:bg-slate-100 dark:hover:bg-zinc-800/80 hover:text-slate-900 dark:hover:text-white"
                      }`}
                    >
                      <TrendingUp size={16} className={adminSubTab === "financeiro_dre" || adminSubTab === "fluxo_financeiro" ? "text-emerald-950" : "text-emerald-600 dark:text-emerald-400"} />
                      <span>2. Financeiro & DRE</span>
                    </button>

                    <button
                      type="button"
                      onClick={() => { setAdminSubTab("gateways_parametros"); setIsSidebarOpen(false); }}
                      className={`w-full px-3.5 py-2.5 rounded-xl font-display font-bold text-xs transition flex items-center gap-3 cursor-pointer ${
                        adminSubTab === "gateways_parametros" || adminSubTab === "bancos" || adminSubTab === "configuracoes"
                          ? "bg-amber-400 text-emerald-950 shadow-md font-extrabold"
                          : "text-slate-700 dark:text-zinc-300 hover:bg-slate-100 dark:hover:bg-zinc-800/80 hover:text-slate-900 dark:hover:text-white"
                      }`}
                    >
                      <CreditCard size={16} className={adminSubTab === "gateways_parametros" || adminSubTab === "bancos" || adminSubTab === "configuracoes" ? "text-emerald-950" : "text-amber-500 dark:text-amber-400"} />
                      <span>3. Gateways & Parâmetros</span>
                    </button>

                    <button
                      type="button"
                      onClick={() => { setAdminSubTab("torneios_suporte"); setIsSidebarOpen(false); }}
                      className={`w-full px-3.5 py-2.5 rounded-xl font-display font-bold text-xs transition flex items-center gap-3 cursor-pointer ${
                        adminSubTab === "torneios_suporte" || adminSubTab === "torneios"
                          ? "bg-amber-400 text-emerald-950 shadow-md font-extrabold"
                          : "text-slate-700 dark:text-zinc-300 hover:bg-slate-100 dark:hover:bg-zinc-800/80 hover:text-slate-900 dark:hover:text-white"
                      }`}
                    >
                      <Trophy size={16} className={adminSubTab === "torneios_suporte" || adminSubTab === "torneios" ? "text-emerald-950" : "text-amber-500 dark:text-amber-300"} />
                      <span>4. Torneios & Suporte</span>
                    </button>

                    <button
                      type="button"
                      onClick={() => { setAdminSubTab("seguranca_moderacao"); setIsSidebarOpen(false); }}
                      className={`w-full px-3.5 py-2.5 rounded-xl font-display font-bold text-xs transition flex items-center gap-3 cursor-pointer ${
                        adminSubTab === "seguranca_moderacao" || adminSubTab === "bloqueios" || adminSubTab === "denuncias_arenas"
                          ? "bg-amber-400 text-emerald-950 shadow-md font-extrabold"
                          : "text-slate-700 dark:text-zinc-300 hover:bg-slate-100 dark:hover:bg-zinc-800/80 hover:text-slate-900 dark:hover:text-white"
                      }`}
                    >
                      <Shield size={16} className={adminSubTab === "seguranca_moderacao" || adminSubTab === "bloqueios" || adminSubTab === "denuncias_arenas" ? "text-emerald-950" : "text-rose-500 dark:text-rose-400"} />
                      <span>5. Segurança & Moderação</span>
                    </button>

                    <div className="pt-4 mt-4 border-t border-slate-200 dark:border-zinc-800">
                      <button
                        type="button"
                        onClick={() => { setActiveTab("hub"); setIsSidebarOpen(false); }}
                        className="w-full px-3.5 py-2.5 rounded-xl font-display font-bold text-xs bg-rose-50 dark:bg-rose-950/60 hover:bg-rose-100 dark:hover:bg-rose-900 border border-rose-200 dark:border-rose-800 text-rose-700 dark:text-rose-300 transition flex items-center justify-center gap-2 cursor-pointer shadow-sm"
                      >
                        <LogOut size={16} />
                        <span>Sair do Admin (Voltar ao Site)</span>
                      </button>
                    </div>
                  </nav>

                  {/* Seletor Visual de Temas (Claro / Escuro) */}
                  <div className="mt-4 p-3 bg-slate-50 dark:bg-zinc-900/60 border border-slate-200 dark:border-zinc-800 rounded-2xl">
                    <ThemeSelector
                      currentTheme={theme}
                      onThemeChange={setTheme}
                      variant="full"
                    />
                  </div>
                </div>
              ) : (
                /* NAVEGAÇÃO DE USUÁRIOS E ATLETAS */
                <div>
                  <div className="flex items-center justify-between py-1 pb-4 border-b border-slate-200 dark:border-emerald-900/40">
                    <div className="flex items-center flex-1 min-w-0 pr-3">
                      <img
                        src="/branding/hub-esportivo-logo.svg"
                        alt="Hub Esportivo"
                        className="h-16 sm:h-18 w-auto max-w-full object-contain object-left pointer-events-none select-none"
                      />
                    </div>
                    <button
                      type="button"
                      onClick={() => setIsSidebarOpen(false)}
                      className="p-1.5 rounded-lg hover:bg-slate-100 dark:hover:bg-emerald-950 text-slate-500 dark:text-emerald-400 hover:text-slate-900 dark:hover:text-white transition cursor-pointer"
                    >
                      <X size={18} />
                    </button>
                  </div>

                  {/* Seção de Autenticação Google & Perfil Ativo */}
                  <div className="mt-4 p-3.5 rounded-2xl bg-slate-50 dark:bg-emerald-950/70 border border-slate-200 dark:border-emerald-800/70 space-y-2.5 shadow-sm">
                    {user ? (
                      <div className="space-y-2.5">
                        <div
                          onClick={() => { setActiveTab("perfil"); setIsSidebarOpen(false); }}
                          className="flex items-center gap-3 p-1.5 -m-1.5 rounded-xl hover:bg-slate-200/60 dark:hover:bg-emerald-900/80 transition cursor-pointer group"
                          title="Clique para abrir e editar o Perfil"
                        >
                          {user.photoURL || userProfile.avatarUrl ? (
                            <img
                              src={user.photoURL || userProfile.avatarUrl}
                              alt={userProfile.name || "Avatar"}
                              className="w-10 h-10 rounded-full border-2 border-amber-400 object-cover shrink-0 group-hover:scale-105 transition"
                              referrerPolicy="no-referrer"
                            />
                          ) : (
                            <div className="w-10 h-10 rounded-full bg-emerald-100 dark:bg-emerald-800 text-emerald-800 dark:text-amber-300 font-black flex items-center justify-center text-sm border-2 border-emerald-300 dark:border-emerald-600 shrink-0 group-hover:scale-105 transition">
                              {userProfile.name ? userProfile.name.slice(0, 2).toUpperCase() : "U"}
                            </div>
                          )}
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center justify-between">
                              <p className="text-xs font-black text-slate-900 dark:text-white truncate group-hover:text-amber-600 dark:group-hover:text-amber-300 transition">
                                {userProfile.name || "Minha Conta"}
                              </p>
                              <Settings size={13} className="text-amber-500 dark:text-amber-400 opacity-80 group-hover:opacity-100 shrink-0 ml-1" />
                            </div>
                            <p className="text-[10px] font-mono font-medium text-slate-500 dark:text-emerald-300 truncate">
                              {user.email || userProfile.email || "Conectado"}
                            </p>
                          </div>
                        </div>

                        <div className="flex items-center justify-between pt-2 border-t border-slate-200 dark:border-emerald-900/60 text-[10px]">
                          <button
                            type="button"
                            onClick={() => { setActiveTab("perfil"); setIsSidebarOpen(false); }}
                            className="font-extrabold uppercase px-2.5 py-1 rounded-lg bg-amber-400/10 dark:bg-amber-400/20 text-amber-700 dark:text-amber-300 border border-amber-400/30 hover:bg-amber-400/20 transition cursor-pointer flex items-center gap-1"
                          >
                            <span>{userProfile.accountType === "dono_arena" ? "🏟️ Dono de Arena" : "🏃‍♂️ Atleta"}</span>
                            <span className="text-[9px] text-amber-800 dark:text-amber-200 underline font-bold">• Perfil</span>
                          </button>

                          <button
                            type="button"
                            onClick={handleGoogleLogout}
                            className="text-[10px] font-bold text-rose-600 dark:text-rose-300 hover:text-rose-700 dark:hover:text-rose-100 flex items-center gap-1 transition cursor-pointer px-1 py-0.5"
                            title="Sair da Conta"
                          >
                            <LogOut size={12} />
                            <span>Sair</span>
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="space-y-2">
                        <button
                          type="button"
                          onClick={() => {
                            if (onOpenAuthModal) {
                              onOpenAuthModal();
                            } else if (handleGoogleLogin) {
                              handleGoogleLogin();
                            }
                            setIsSidebarOpen(false);
                          }}
                          className="w-full bg-amber-400 hover:bg-amber-300 active:scale-95 text-emerald-950 font-extrabold text-xs py-3 px-3.5 rounded-xl shadow-md transition cursor-pointer flex items-center justify-center gap-2"
                        >
                          <LogIn size={16} />
                          <span>Entrar</span>
                        </button>

                        <button
                          type="button"
                          onClick={() => {
                            setIsSidebarOpen(false);
                            window.history.pushState({}, "", "/");
                            window.dispatchEvent(new Event("popstate"));
                          }}
                          className="w-full bg-slate-100 dark:bg-zinc-900 hover:bg-slate-200 dark:hover:bg-zinc-800 text-slate-700 dark:text-zinc-300 font-bold text-xs py-2.5 px-3.5 rounded-xl border border-slate-200 dark:border-zinc-800 transition cursor-pointer flex items-center justify-center gap-2"
                        >
                          <span>🏠 Tela Inicial</span>
                        </button>
                      </div>
                    )}
                  </div>

                  <nav className="mt-4 space-y-1.5">
                    <button
                      type="button"
                      onClick={() => { setActiveTab("hub"); setIsSidebarOpen(false); }}
                      className={`w-full px-3.5 py-2.5 rounded-xl font-display font-bold text-xs transition flex items-center gap-3 cursor-pointer ${
                        activeTab === "hub"
                          ? "bg-amber-400 text-emerald-950 shadow-md font-extrabold"
                          : "text-slate-700 dark:text-emerald-300 hover:bg-slate-100 dark:hover:bg-emerald-950/60 hover:text-slate-900 dark:hover:text-white"
                      }`}
                    >
                      <Sparkles size={16} />
                      <span>HUB Principal</span>
                    </button>

                    {userProfile.accountType !== "dono_arena" && (
                      <button
                        type="button"
                        onClick={() => { setActiveTab("jogo"); setIsSidebarOpen(false); }}
                        className={`w-full px-3.5 py-2.5 rounded-xl font-display font-bold text-xs transition flex items-center gap-3 cursor-pointer ${
                          activeTab === "jogo" || activeTab === "racha"
                            ? "bg-amber-400 text-emerald-950 shadow-md font-extrabold"
                            : "text-slate-700 dark:text-emerald-300 hover:bg-slate-100 dark:hover:bg-emerald-950/60 hover:text-slate-900 dark:hover:text-white"
                        }`}
                      >
                        <CloudLightning size={16} />
                        <span>Sorteio Racha</span>
                      </button>
                    )}

                    <button
                      type="button"
                      onClick={() => { setActiveTab("quadras"); setIsSidebarOpen(false); }}
                      className={`w-full px-3.5 py-2.5 rounded-xl font-display font-bold text-xs transition flex items-center gap-3 cursor-pointer ${
                        activeTab === "quadras"
                          ? "bg-amber-400 text-emerald-950 shadow-md font-extrabold"
                          : "text-slate-700 dark:text-emerald-300 hover:bg-slate-100 dark:hover:bg-emerald-950/60 hover:text-slate-900 dark:hover:text-white"
                      }`}
                    >
                      <Building2 size={16} />
                      <span>{userProfile.accountType === "dono_arena" ? "Minhas Quadras" : "Reservar Quadras"}</span>
                    </button>

                    {userProfile.accountType === "dono_arena" && (
                      <>
                        <button
                          type="button"
                          id="sidebar-nav-vendas"
                          onClick={() => { setActiveTab("vendas"); setIsSidebarOpen(false); }}
                          className={`w-full px-3.5 py-2.5 rounded-xl font-display font-bold text-xs transition flex items-center gap-3 cursor-pointer ${
                            activeTab === "vendas"
                              ? "bg-amber-400 text-emerald-950 shadow-md font-extrabold"
                              : "text-slate-700 dark:text-emerald-300 hover:bg-slate-100 dark:hover:bg-emerald-950/60 hover:text-slate-900 dark:hover:text-white"
                          }`}
                        >
                          <ShoppingBag size={16} />
                          <span>Vendas & Loja (PDV)</span>
                        </button>

                        <button
                          type="button"
                          id="sidebar-nav-horarios-eventos"
                          onClick={() => { setActiveTab("dayuse"); setIsSidebarOpen(false); }}
                          className={`w-full px-3.5 py-2.5 rounded-xl font-display font-bold text-xs transition flex items-center gap-3 cursor-pointer ${
                            activeTab === "dayuse" || activeTab === "mensalistas"
                              ? "bg-amber-400 text-emerald-950 shadow-md font-extrabold"
                              : "text-slate-700 dark:text-emerald-300 hover:bg-slate-100 dark:hover:bg-emerald-950/60 hover:text-slate-900 dark:hover:text-white"
                          }`}
                        >
                          <CalendarDays size={16} />
                          <span>Horários & Eventos</span>
                        </button>

                        <button
                          type="button"
                          id="sidebar-nav-financeiro"
                          onClick={() => { setActiveTab("financeiro"); setIsSidebarOpen(false); }}
                          className={`w-full px-3.5 py-2.5 rounded-xl font-display font-bold text-xs transition flex items-center gap-3 cursor-pointer ${
                            activeTab === "financeiro"
                              ? "bg-amber-400 text-emerald-950 shadow-md font-extrabold"
                              : "text-slate-700 dark:text-emerald-300 hover:bg-slate-100 dark:hover:bg-emerald-950/60 hover:text-slate-900 dark:hover:text-white"
                          }`}
                        >
                          <DollarSign size={16} />
                          <span>Financeiro da Arena</span>
                        </button>
                      </>
                    )}

                    <button
                      type="button"
                      onClick={() => { setActiveTab("torneios"); setIsSidebarOpen(false); }}
                      className={`w-full px-3.5 py-2.5 rounded-xl font-display font-bold text-xs transition flex items-center gap-3 cursor-pointer ${
                        activeTab === "torneios"
                          ? "bg-amber-400 text-emerald-950 shadow-md font-extrabold"
                          : "text-slate-700 dark:text-emerald-300 hover:bg-slate-100 dark:hover:bg-emerald-950/60 hover:text-slate-900 dark:hover:text-white"
                      }`}
                    >
                      <Trophy size={16} />
                      <span>Torneios</span>
                    </button>

                    <button
                      type="button"
                      onClick={() => { setActiveTab("historico"); setIsSidebarOpen(false); }}
                      className={`w-full px-3.5 py-2.5 rounded-xl font-display font-bold text-xs transition flex items-center gap-3 cursor-pointer ${
                        activeTab === "historico"
                          ? "bg-amber-400 text-emerald-950 shadow-md font-extrabold"
                          : "text-slate-700 dark:text-emerald-300 hover:bg-slate-100 dark:hover:bg-emerald-950/60 hover:text-slate-900 dark:hover:text-white"
                      }`}
                    >
                      <History size={16} />
                      <span>Histórico & Súmulas</span>
                    </button>

                    {isAdminMode && (
                      <button
                        type="button"
                        onClick={() => { setActiveTab("admin"); setIsSidebarOpen(false); }}
                        className={`w-full px-3.5 py-2.5 rounded-xl font-display font-bold text-xs transition flex items-center gap-3 cursor-pointer ${
                          activeTab === "admin"
                            ? "bg-amber-400 text-emerald-950 shadow-md font-extrabold"
                            : "text-amber-700 dark:text-amber-300 hover:bg-amber-400/20 border border-amber-400/40"
                        }`}
                      >
                        <Shield size={16} className="text-amber-500 dark:text-amber-400" />
                        <span>Painel Super Admin</span>
                      </button>
                    )}
                  </nav>

                  {/* Seletor Visual de Temas (Claro / Escuro) */}
                  <div className="mt-4 p-3 bg-slate-50 dark:bg-emerald-950/80 border border-slate-200 dark:border-emerald-800/80 rounded-2xl">
                    <ThemeSelector
                      currentTheme={theme}
                      onThemeChange={setTheme}
                      variant="full"
                    />
                  </div>

                  {/* Controle de Zoom da Interface */}
                  <div className="mt-4 p-3 bg-slate-50 dark:bg-emerald-950/80 border border-slate-200 dark:border-emerald-800/80 rounded-2xl space-y-2 shadow-inner">
                    <div className="flex items-center justify-between text-xs text-slate-700 dark:text-emerald-300 font-bold">
                      <span className="flex items-center gap-1.5"><ZoomIn size={14} /> Zoom da Tela</span>
                      <span className="font-mono text-amber-600 dark:text-amber-300 font-black">{zoomLevel}%</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <button
                        type="button"
                        onClick={handleZoomOut}
                        disabled={zoomLevel <= 75}
                        className="flex-1 py-1.5 bg-slate-200 dark:bg-emerald-900/60 hover:bg-slate-300 dark:hover:bg-emerald-800 text-slate-800 dark:text-white rounded-lg text-xs font-extrabold transition disabled:opacity-40 flex items-center justify-center gap-1 cursor-pointer"
                      >
                        <ZoomOut size={13} /> -10%
                      </button>
                      <button
                        type="button"
                        onClick={handleResetZoom}
                        className="px-2.5 py-1.5 bg-amber-400/20 hover:bg-amber-400/30 text-amber-700 dark:text-amber-300 rounded-lg text-xs font-extrabold transition cursor-pointer"
                        title="Zoom Padrão (100%)"
                      >
                        100%
                      </button>
                      <button
                        type="button"
                        onClick={handleZoomIn}
                        disabled={zoomLevel >= 140}
                        className="flex-1 py-1.5 bg-slate-200 dark:bg-emerald-900/60 hover:bg-slate-300 dark:hover:bg-emerald-800 text-slate-800 dark:text-white rounded-lg text-xs font-extrabold transition disabled:opacity-40 flex items-center justify-center gap-1 cursor-pointer"
                      >
                        <ZoomIn size={13} /> +10%
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Footer of Mobile Drawer */}
            <div className="pt-4 border-t border-slate-200 dark:border-emerald-900/40 space-y-4">
              <div className="flex items-center gap-2">
                <span className={`h-2 w-2 rounded-full ${isFirebaseConfigured() ? "bg-amber-500 animate-pulse" : "bg-emerald-500"}`} />
                <span className="text-[10px] font-mono font-bold tracking-wide uppercase text-slate-600 dark:text-emerald-300">
                  {isFirebaseConfigured() ? "Nuvem Ativa" : "Local Storage"}
                </span>
              </div>

              <div className="space-y-2">
                <button
                  type="button"
                  onClick={() => { handleResetAllData(); setIsSidebarOpen(false); }}
                  className="w-full text-left text-[11px] bg-rose-50 dark:bg-rose-950/40 hover:bg-rose-100 dark:hover:bg-rose-900/60 border border-rose-200 dark:border-rose-900 text-rose-700 dark:text-rose-300 px-3 py-2 rounded-lg flex items-center gap-2 transition cursor-pointer"
                >
                  <RotateCcw size={12} />
                  <span>Limpar Todos os Dados</span>
                </button>

                <div className="relative">
                  <label
                    htmlFor="mobile-file-backup"
                    className="text-left text-[11px] bg-slate-100 dark:bg-emerald-950/60 hover:bg-slate-200 dark:hover:bg-emerald-900 border border-slate-200 dark:border-emerald-800 text-slate-700 dark:text-emerald-300 px-3 py-2 rounded-lg flex items-center gap-2 transition cursor-pointer w-full"
                  >
                    <Upload size={12} />
                    <span>Importar Backup</span>
                  </label>
                  <input
                    id="mobile-file-backup"
                    type="file"
                    accept=".json"
                    onChange={(e) => { handleImportJSON(e); setIsSidebarOpen(false); }}
                    className="hidden"
                  />
                </div>
              </div>
            </div>
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
};
